import { Box, InputLabel, ListSubheader, MenuItem, Select, useTheme } from '@mui/material';
import React from 'react'
import LockIcon from './LockIcon';
import { SitesResponse, SlateResponse, SportsResponse } from './store/types';
import { CustomToolTip } from './CustomToolTip';
import { SubscriptionTierEnum } from './enums';

interface IInputProps {
  readonly data: MenuOption<SportsResponse | SitesResponse | SlateResponse | any>[]
  readonly label: string;
  readonly width?: number;
  readonly bottomLabel?: string;
  readonly value: string | number;
  readonly onSelect: (value: any) => void;
  readonly tierLock?: boolean;
  readonly groupings?: { name: string; groupByValue: string; }[];
}
type InputProps = IInputProps;

export const SelectInput = (props: InputProps) => {
  const { data, label, bottomLabel, value, tierLock, groupings, onSelect } = props;
  const theme = useTheme();
  const id = `input-label-${label}`;

  return (
    <Box sx={{
      marginRight: '16px',
      color: theme.palette.common.white,
      textOverflow: 'wrap',
      [theme.breakpoints.down('lg')]: {
        width: '110px',
        marginRight: '10px',
      },
      [theme.breakpoints.down('sm')]: {
        width: '90px',
      },
      '& .MuiFormLabel-root': {
        color: theme.palette.common.white,
        opacity: 0.7,
        paddingLeft: '4px',
        fontFamily: 'Inter',
        fontSize: '12px',
        fontStyle: 'normal',
        fontHeight: 500,
      },
      '& .MuiInputBase-root': {
        color: 'white',
        backgroundColor: '#000A16',
        borderRadius: '6px',
        padding: '4px 0px',
      },
      '& ul': {
        display: 'flex',
        flexDirection: 'column',
      },
    }}>
      {/* <Hidden lgDown> */}
      {!bottomLabel ? (
        <InputLabel id={id}>{label}</InputLabel>
      ) : (
        <CustomToolTip title={bottomLabel} placement='bottom'>
          <InputLabel id={id}>{label}</InputLabel>
        </CustomToolTip>
      )}
      
      {/* </Hidden> */}
      <Select
        labelId={id}
        value={value || ''}
        sx={{
          height: 36,
          color: theme.palette.common.white,
          borderRadius: 6,
          background: '#0A0A0A',
          paddingTop: 8,
          fontSize: 10,
          width: '100%',
          '& .MuiSvgIcon-root': {
            color: theme.palette.common.white,
          }
        }}
        variant='outlined'
      >
        {
          groupings && groupings.length > 0 ? groupings.flatMap((group, i: number) => {
            // I can't return a React.Fragment here with the grouping and mapped items together because the MUI Select component doesn't like that
            // SO I have to return an array of JSX elements and then spread them into the Select component
            const groupedItems = data.filter((y: MenuOption<SlateResponse>) => y.option.type?.toLowerCase() === group.groupByValue?.toLowerCase()) || [];
            const groupComponents: JSX.Element[] = [];
            const subHeader = <ListSubheader>{group.name}</ListSubheader>;
            groupComponents.push(subHeader);

            const items = groupedItems.map((x, j: number) => (
              <MenuItem
                key={x.name + i}
                value={x.value}
                onClick={() => {
                  if (!(x.lock && tierLock)) {
                    onSelect(x.option);
                  }
                }}
                disabled={x.lock && tierLock}
                style={{ width: '100%', padding: '6px 16px !important' }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" sx={{ cursor: x.lock && tierLock ? "not-allowed" : "pointer", overflow: 'hidden', }}>
                  <Box display="flex" alignItems="center" fontSize={12}>
                    {
                      x.logo && (
                        <Box height="16px" width="16px" marginRight="4px" sx={{ backgroundColor: x.invertBackground ? 'white' : '' }}>
                          <img src={x.logo} alt={x.name} style={{ width: '100%', height: '100%', objectFit: 'contain', }} />
                        </Box>
                      )
                    }
                    {x.name}
                  </Box>
                  {
                    x.lock && tierLock && (
                      <LockIcon />
                    )
                  }
                </Box>
              </MenuItem>
            ))
            groupComponents.push(...items);

            return groupComponents;
          }) : (
            data && data.map((x, i: number) => (
              <MenuItem
                key={x.name + i}
                value={x.value}
                onClick={() => {
                  if (!(x.lock && tierLock)) {
                    onSelect(x.option);
                  }
                }}
                disabled={x.lock && tierLock}
                sx={{ width: '100%', padding: '6px 16px !important' }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" sx={{ cursor: x.lock && tierLock ? "not-allowed" : "pointer", overflow: 'hidden', }}>
                  <Box display="flex" alignItems="center" fontSize={12}>
                    {
                      x.logo && (
                        <Box height="16px" width="16px" marginRight="4px" sx={{ backgroundColor: x.invertBackground ? 'white' : '' }}>
                          <img src={x.logo} alt={x.name} style={{ width: '100%', height: '100%', objectFit: 'contain', }} />
                        </Box>
                      )
                    }
                    {x.name}
                  </Box>
                  {
                    x.lock && tierLock && (
                      <LockIcon />
                    )
                  }
                </Box>
              </MenuItem>
            ))
          )
        }
      </Select>
      {/* <Hidden lgDown>
        {bottomLabel && (
          <InputLabel id={id}>{bottomLabel}</InputLabel>
        )}
      </Hidden> */}
    </Box>
  )
}


export interface MenuOption<T> {
  readonly name: string;
  readonly value: string | number;
  readonly option: T;
  readonly invertBackground?: boolean;
  readonly lock?: boolean;
  readonly logo?: string;
}

export const lineupBuilderPoolSizeOptions: MenuOption<any>[] = [
  {
    name: 'Small Contest (<1000 lineups)',
    value: 250,
    option: { value: 250 },
  },
  {
    name: 'Large Contest (>1000 lineups)',
    value: 1000,
    option: { value: 1000 },
  },
]

export const poolSizeOptions = (
  sport?: string,
  slateType?: string,
  maxEntries?: number,
  allowedEntryCounts?: number[],
  authTier?: string
): MenuOption<any>[] => {
  const isMvp = authTier === SubscriptionTierEnum[SubscriptionTierEnum.MVP];
  const isSimsMax = authTier === SubscriptionTierEnum[SubscriptionTierEnum.SIMS_MAX];
  const hasPremiumAccess = isSimsMax || isMvp;
  const hasMvpAccess = isMvp;
  const isShowdown = slateType?.toLowerCase() === 'showdown';

  // If backend provides options, use them (but still apply frontend tier logic for display)
  // MVP-only options must be HIDDEN (not locked) for non-MVP users to match the non-backend path
  if (allowedEntryCounts && allowedEntryCounts.length > 0) {
    // Filter backend counts to match tier access (MVP options should be hidden, not locked)
    let filteredCounts = allowedEntryCounts.filter(count => {
      // MVP-only counts (15k, 20k): only shown to MVP users
      if (count === 15000 || count === 20000) {
        return hasMvpAccess;
      }
      // 25k/50k: MVP gets them (50k showdown only), SIMS_MAX gets them on NFL showdown or MMA only
      if (count === 25000 || count === 50000) {
        if (hasMvpAccess) {
          return count === 25000 || isShowdown; // MVP: 25k always, 50k showdown only
        }
        if (isSimsMax) {
          const sportLower = sport?.toLowerCase();
          const isMma = sportLower === 'mma';
          const isNfl = sportLower === 'nfl';
          // MMA: 25k/50k on both classic and showdown
          // NFL: 25k/50k on showdown only
          if (isMma || (isNfl && isShowdown)) {
            return true;
          }
        }
        return false; // Others: no 25k/50k
      }
      return true; // All other counts pass through
    });

    // Apply sport-specific filtering for non-MVP users (SIMS_MAX caps)
    // NBA/MLB/CFB: capped at 5000 (no 25k/50k even on showdown)
    if (!hasMvpAccess && sport && ['nba', 'mlb', 'cfb'].includes(sport.toLowerCase())) {
      filteredCounts = filteredCounts.filter(count => count <= 5000);
    }

    let options = filteredCounts.map(count => ({
      name: count.toString(),
      value: count,
      option: { value: count },
      lock: !hasPremiumAccess && count > 2000
    }));

    if (maxEntries) {
      options = options.filter(x => (x.value as number) < maxEntries);
    }

    return options.sort((a, b) => (a.value as number) - (b.value as number));
  }

  // Base options available to all tiers
  let options: MenuOption<any>[] = [
    { name: '250', value: 250, option: { value: 250 }, lock: false },
    { name: '500', value: 500, option: { value: 500 }, lock: false },
    { name: '1000', value: 1000, option: { value: 1000 }, lock: false },
    { name: '1500', value: 1500, option: { value: 1500 }, lock: false },
    { name: '2000', value: 2000, option: { value: 2000 }, lock: false },
    { name: '5000', value: 5000, option: { value: 5000 }, lock: !hasPremiumAccess },
    { name: '10000', value: 10000, option: { value: 10000 }, lock: !hasPremiumAccess },
  ];

  // SIMS_MAX (non-MVP) gets 25k/50k on NFL showdown or MMA (both classic and showdown)
  if (isSimsMax && !hasMvpAccess) {
    const sportLower = sport?.toLowerCase();
    const isMma = sportLower === 'mma';
    const isNfl = sportLower === 'nfl';
    if (isMma || (isNfl && isShowdown)) {
      options.push({ name: '25000', value: 25000, option: { value: 25000 }, lock: false });
      options.push({ name: '50000', value: 50000, option: { value: 50000 }, lock: false });
    }
  }

  // MVP-only options: HIDDEN (not shown) for non-MVP users
  if (hasMvpAccess) {
    options.push({ name: '15000', value: 15000, option: { value: 15000 }, lock: false });
    options.push({ name: '20000', value: 20000, option: { value: 20000 }, lock: false });
    options.push({ name: '25000', value: 25000, option: { value: 25000 }, lock: false });
    // 50k only on showdowns for MVP
    if (isShowdown) {
      options.push({ name: '50000', value: 50000, option: { value: 50000 }, lock: false });
    }
  }

  // Apply sport-specific filtering for non-MVP users (SIMS_MAX caps)
  // NBA/MLB/CFB: capped at 5000 (no 25k/50k even on showdown)
  if (!hasMvpAccess && sport && ['nba', 'mlb', 'cfb'].includes(sport.toLowerCase())) {
    options = options.filter(x => (x.value as number) <= 5000);
  }

  if (maxEntries) {
    options = options.filter(x => x.value as number < maxEntries);
  }

  // Sort by value and return
  return options.sort((a, b) => (a.value as number) - (b.value as number));
};

export const poolSourceOptions = (enableLiveContest: boolean): MenuOption<any>[] => {
  const options = [
    {
      name: 'Upload Custom Pool',
      value: 'CustomPool',
      option: { value: 'CustomPool' },
    },
    {
      name: 'Generate Pool',
      value: 'GeneratePool',
      option: { value: 'GeneratePool' },
    },
    {
      name: 'Select Live Contest Pool',
      value: 'LiveContest',
      option: { value: 'LiveContest' },
    },
  ];
  return options.filter(x => x.value !== 'LiveContest' || enableLiveContest);
}

export const percentToFirstOptions: MenuOption<any>[] = [
  {
    name: '5%',
    value: 5,
    option: { value: 5 },
  },
  {
    name: '10%',
    value: 10,
    option: { value: 10 },
  },
  {
    name: '15%',
    value: 15,
    option: { value: 15 },
  },
  {
    name: '20%',
    value: 20,
    option: { value: 20 },
  },
  {
    name: '25%',
    value: 25,
    option: { value: 25 },
  },
  {
    name: '30%',
    value: 30,
    option: { value: 30 },
  },
  {
    name: '35%',
    value: 35,
    option: { value: 35 },
  },
  {
    name: '40%',
    value: 40,
    option: { value: 40 },
  }
]
